/* eslint-disable no-undef */
import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import {
  InlineNotification,
  Loading,
  ToastNotification
} from 'carbon-components-react';
import { AreaCustom16, Upload16 } from '@carbon/icons-react';
import {
  OptionCardGroup,
  FileUploadDropZone,
  UploadedFileRow
} from 'components/PremiumWizard';
import { MemberLookupWizardInputMode } from '../hooks/useBydaMemberLookupWizard';
import useMemberLookupMap, {
  DrawingTool,
  DrawingResult
} from '../hooks/useMemberLookupMap';
import { DrawingValidationError } from 'utilities/geometry-validation.util';
import { parseMemberLookupUploadError } from '../constants/errorMessages';
import { LandParcelType } from 'types';
import MemberLookupDrawingTooltip from './MemberLookupDrawingTooltip';
import MemberLookupLiveAreaTag from './MemberLookupLiveAreaTag';
import MemberLookupMapPanel from './MemberLookupMapPanel';
import MemberLookupMapDrawStats from './MemberLookupMapDrawStats';
import FileUploadValidationStats from './FileUploadValidationStats';

interface UploadedFile {
  name: string;
  size: string;
}

interface AreaInputStepProps {
  inputMode: MemberLookupWizardInputMode;
  onInputModeChange: (mode: MemberLookupWizardInputMode) => void;
  /** True once a member lookup operation is active — locks the mode selector */
  isMemberLookupOperationActive: boolean;
  /** True while the S3 upload or pre-payment file validation is in flight */
  isUploading: boolean;
  /** Label shown on the file row while isUploading is true */
  uploadStatusLabel: string;
  /** Error message from the S3 upload or prepare-file step, if any */
  uploadError: string | null;
  /** Error from the MAP_SKETCH register call, shown inline above the map */
  registerError?: string | null;
  /** True once pre-payment file validation has completed */
  isFileValidated?: boolean;
  /** Shape/area summary from GET /{lookupId}/status after file validation */
  fileValidationStats?: { shapeCount: number; areaKm2: number } | null;
  uploadedFile: UploadedFile | null;
  onFileSelected: (file: File) => void;
  onFileReplace: () => void;
  drawnShapes: DrawingResult[];
  onDrawingComplete: (result: DrawingResult) => void;
  onShapeUpdated: (index: number, result: DrawingResult) => void;
  onClearShape: (index: number) => void;
  onClearAllShapes: () => void;
}

const AREA_OPTIONS = [
  {
    id: 'upload',
    icon: <Upload16 />,
    title: 'Upload file',
    description:
      'Upload a file containing your project boundaries. Supported formats: GeoJSON, GeoPackage, KMZ, SHP (ZIP) and TAB (ZIP).'
  },
  {
    id: 'draw',
    icon: <AreaCustom16 />,
    title: 'Draw on the map',
    description: 'Draw your project area directly on the interactive map.'
  }
];

const DrawMapSection: React.FC<{
  drawnShapes: DrawingResult[];
  onDrawingComplete: (result: DrawingResult) => void;
  onShapeUpdated: (index: number, result: DrawingResult) => void;
  onClearShape: (index: number) => void;
  onClearAll: () => void;
  registerError: string | null;
}> = ({
  drawnShapes,
  onDrawingComplete,
  onShapeUpdated,
  onClearShape,
  onClearAll,
  registerError
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [drawingError, setDrawingError] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [toolExpanded, setToolExpanded] = useState<boolean>(true);

  const handleDrawingError = useCallback((error: DrawingValidationError) => {
    const hasCustomTitle =
      error.name &&
      error.name !== 'Error' &&
      error.name !== 'DrawingValidationError';

    setDrawingError({
      title: hasCustomTitle ? error.name : 'Error',
      message: error.message
    });
  }, []);

  const handleDrawingComplete = useCallback(
    (result: DrawingResult) => {
      setDrawingError(null);
      onDrawingComplete(result);
    },
    [onDrawingComplete]
  );

  const handleShapeUpdated = useCallback(
    (index: number, result: DrawingResult) => {
      setDrawingError(null);
      onShapeUpdated(index, result);
    },
    [onShapeUpdated]
  );

  const {
    isReady,
    isMapLoading,
    isDrawing,
    activeTool,
    liveArea,
    view,
    sketchViewModel,
    startDrawing,
    cancelDrawing,
    clearDrawing,
    addLandParcel
  } = useMemberLookupMap({
    containerRef: mapContainerRef,
    onDrawingComplete: handleDrawingComplete,
    onDrawingError: handleDrawingError,
    onShapeDeleted: onClearShape,
    onShapeUpdated: handleShapeUpdated,
    existingShapes: drawnShapes
  });

  /**
   * Re-expand the search + draw panel when the user returns to selector mode.
   * Mirrors lodge-enquiry behaviour where search reappears after drawing completes.
   */
  useEffect(() => {
    if (activeTool === 'selector' && !isDrawing) {
      setToolExpanded(true);
    }
  }, [activeTool, isDrawing]);

  const handleToolClick = (tool: DrawingTool) => {
    setDrawingError(null);
    if (tool === activeTool) {
      cancelDrawing();
      setToolExpanded(true);
    } else {
      startDrawing(tool);
      setToolExpanded(false);
    }
  };

  const handleClearAll = () => {
    clearDrawing();
    onClearAll();
    setDrawingError(null);
    setToolExpanded(true);
  };

  const handleUseLandParcel = useCallback(
    (parcel: LandParcelType) => {
      setDrawingError(null);
      addLandParcel(parcel);
    },
    [addLandParcel]
  );

  const handleCancelLandParcel = useCallback(() => {
    startDrawing('polygon');
    setToolExpanded(false);
  }, [startDrawing]);

  const totalAreaKm2 = useMemo(
    () => drawnShapes.reduce((sum, shape) => sum + shape.area, 0),
    [drawnShapes]
  );

  return (
    <div>
      <div className="byda-member-lookup__area-input-map-wrapper map-container">
        <div
          ref={mapContainerRef}
          className="byda-member-lookup__area-input-map-container map"
          style={{ visibility: isMapLoading ? 'hidden' : 'visible' }}
        />

        {isMapLoading && (
          <div className="byda-member-lookup__area-input-map-loader">
            <Loading description="Loading map" withOverlay={false} />
          </div>
        )}

        <MemberLookupMapPanel
          isReady={isReady}
          activeTool={activeTool}
          toolExpanded={toolExpanded}
          hasDrawnShapes={drawnShapes.length > 0}
          onExpandTools={() => setToolExpanded(true)}
          onToolClick={handleToolClick}
          onClearAll={handleClearAll}
          onUseLandParcel={handleUseLandParcel}
          onCancelLandParcel={handleCancelLandParcel}
        />

        {isDrawing && (
          <div className="byda-member-lookup__area-input-map-live-area">
            <MemberLookupLiveAreaTag
              liveArea={liveArea}
              activeTool={activeTool}
            />
          </div>
        )}

        {isDrawing && view && sketchViewModel && (
          <MemberLookupDrawingTooltip
            view={view}
            sketchView={sketchViewModel}
            sketchToolType={activeTool}
          />
        )}

        {drawnShapes.length > 0 && (
          <MemberLookupMapDrawStats
            shapeCount={drawnShapes.length}
            areaKm2={totalAreaKm2}
          />
        )}

        {registerError && (
          <InlineNotification
            kind="error"
            title="Validation failed"
            subtitle={registerError}
            className="byda-member-lookup__area-input-register-error"
          />
        )}

        {drawingError && (
          <div className="byda-member-lookup__area-input-drawing-error">
            <ToastNotification
              kind="error"
              title={drawingError.title}
              caption={drawingError.message}
              timeout={0}
              iconDescription="Close error notification"
              onCloseButtonClick={() => setDrawingError(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ALLOWED_EXTENSIONS = ['.kmz', '.zip', '.geojson', '.gpkg'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const AreaInputStep: React.FC<AreaInputStepProps> = ({
  inputMode,
  onInputModeChange,
  isMemberLookupOperationActive,
  isUploading,
  uploadStatusLabel,
  uploadError,
  uploadedFile,
  onFileSelected,
  onFileReplace,
  drawnShapes,
  onDrawingComplete,
  onShapeUpdated,
  onClearShape,
  onClearAllShapes,
  registerError = null,
  isFileValidated = false,
  fileValidationStats = null
}) => {
  const drawSectionRef = useRef<HTMLDivElement>(null);
  const [fileValidationError, setFileValidationError] = useState<string | null>(
    null
  );

  const uploadErrorNotification = useMemo(
    () => parseMemberLookupUploadError(uploadError),
    [uploadError]
  );

  const disabledOptionIds = useMemo(() => {
    if (isMemberLookupOperationActive) {
      return AREA_OPTIONS.map((option) => option.id).filter(
        (id) => id !== inputMode
      );
    }

    const disabled: string[] = [];
    if (uploadedFile) disabled.push('draw');
    if (drawnShapes.length > 0) disabled.push('upload');
    return disabled;
  }, [uploadedFile, drawnShapes, isMemberLookupOperationActive, inputMode]);

  const handleModeChange = (id: string) => {
    if (!isMemberLookupOperationActive && (id === 'upload' || id === 'draw')) {
      onInputModeChange(id);
    }
  };

  const handleFileReplace = () => {
    setFileValidationError(null);
    onFileReplace();
  };

  /**
   * Smoothly scrolls the map into view when the user selects "Draw on the map".
   *
   * WHY: The map container is tall (nearly full viewport height) and sits below
   * the mode selector cards. Without scrolling, the user may not see the map
   * at all on smaller screens. We scroll only when switching *to* draw mode
   * so we don't disrupt navigation when switching away.
   */
  useEffect(() => {
    function scrollMapIntoView() {
      if (inputMode !== 'draw' || !drawSectionRef.current) return;
      drawSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

    scrollMapIntoView();
  }, [inputMode]);

  return (
    <div>
      <div className="byda-member-lookup__step-header">
        Define your project area
      </div>
      <div className="byda-member-lookup__area-input-sub-header">
        Choose how you&apos;d like to define your project area.
      </div>

      <OptionCardGroup
        options={AREA_OPTIONS}
        selectedId={inputMode}
        onChange={handleModeChange}
        disabledIds={disabledOptionIds}
      />

      {inputMode === 'upload' && (
        <div className="byda-member-lookup__area-input-section">
          <div className="byda-member-lookup__area-input-upload-panel">
            {!uploadedFile ? (
              <>
                {fileValidationError && (
                  <InlineNotification
                    kind="error"
                    lowContrast
                    title="Invalid file"
                    subtitle={fileValidationError}
                    onCloseButtonClick={() => setFileValidationError(null)}
                    className="byda-member-lookup__area-input-notification--mb-md"
                  />
                )}
                <FileUploadDropZone
                  acceptedFormats="GeoJSON, GeoPackage, KMZ, Shapefile (ZIP), TAB (ZIP)"
                  maxSize="10MB"
                  allowedExtensions={ALLOWED_EXTENSIONS}
                  maxSizeBytes={MAX_FILE_SIZE_BYTES}
                  onFileSelected={(file) => {
                    setFileValidationError(null);
                    onFileSelected(file);
                  }}
                  onValidationError={setFileValidationError}
                />
              </>
            ) : (
              <>
                {isFileValidated && !uploadError && (
                  <InlineNotification
                    kind="success"
                    lowContrast
                    hideCloseButton
                    title="File uploaded successfully"
                    className="byda-member-lookup__area-input-notification--mb-md"
                  />
                )}
                {uploadErrorNotification && (
                  <InlineNotification
                    kind={uploadErrorNotification.kind}
                    lowContrast
                    title={uploadErrorNotification.title}
                    subtitle={uploadErrorNotification.subtitle}
                    onCloseButtonClick={handleFileReplace}
                    className="byda-member-lookup__area-input-notification--mt-sm"
                  />
                )}
                <UploadedFileRow
                  filename={uploadedFile.name}
                  metadata={isUploading ? uploadStatusLabel : uploadedFile.size}
                  loading={isUploading}
                  onReplace={handleFileReplace}
                  disabled={isUploading}
                />
                {isFileValidated && fileValidationStats && (
                  <FileUploadValidationStats
                    shapeCount={fileValidationStats.shapeCount}
                    areaKm2={fileValidationStats.areaKm2}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {inputMode === 'draw' && (
        <div
          ref={drawSectionRef}
          className="byda-member-lookup__area-input-section"
        >
          <DrawMapSection
            drawnShapes={drawnShapes}
            onDrawingComplete={onDrawingComplete}
            onShapeUpdated={onShapeUpdated}
            onClearShape={onClearShape}
            onClearAll={onClearAllShapes}
            registerError={registerError}
          />
        </div>
      )}
    </div>
  );
};

export default AreaInputStep;
